// usePyodide — loads Python in a Web Worker so the UI thread stays responsive.
// Reference: /public/codelves/src/app/services/pyodide-runtime.js
// The worker singleton is module-level: Pyodide is downloaded once and shared
// across all hook instances (WorkspacePage, ChatSidebar, NotebookPane).

import { useState, useEffect, useCallback } from 'react';

// ── Module-level singleton ───────────────────────────────────────────────────
let pyWorker = null;
let pyReady = false;
let pyError = null;
const stateListeners = new Set();
const pending = new Map();
let nextId = 0;

function broadcast() {
  stateListeners.forEach((fn) => fn());
}

function ensureWorker() {
  if (pyWorker) return; // Already initialised

  pyWorker = new Worker(
    new URL('../workers/pyodide.worker.js', import.meta.url)
    // Classic worker — uses importScripts inside, no { type: 'module' } needed
  );

  pyWorker.onmessage = (e) => {
    const { type, id, output, error, message, success, packages } = e.data;

    if (type === 'ready') {
      pyReady = true;
      broadcast();
    } else if (type === 'error') {
      pyError = message || 'Failed to load Pyodide';
      broadcast();
    } else if (type === 'result' || type === 'packageResult') {
      // Both runPython results and loadPackage results share the pending map
      const resolve = pending.get(id);
      if (resolve) {
        pending.delete(id);
        if (type === 'result') {
          resolve({ output: output || '', error: error || null });
        } else {
          // packageResult
          resolve({ success: success ?? false, error: error || null, packages: packages || [] });
        }
      }
    }
    // type === 'status': informational only
  };

  pyWorker.onerror = (err) => {
    pyError = err.message || 'Pyodide worker crashed';
    broadcast();
  };

  // Kick off Pyodide download in the background immediately
  pyWorker.postMessage({ type: 'load' });
}
// ────────────────────────────────────────────────────────────────────────────

export default function usePyodide() {
  // Local tick forces re-render when worker state changes
  const [, setTick] = useState(0);

  useEffect(() => {
    const refresh = () => setTick((t) => t + 1);
    stateListeners.add(refresh);
    ensureWorker(); // no-op if already initialised
    return () => stateListeners.delete(refresh);
  }, []);

  const runPython = useCallback(
    (code) => {
      if (!pyReady || !pyWorker) {
        return Promise.resolve({
          output: '',
          error: 'Python runtime not ready yet — please wait a moment.',
        });
      }
      const id = nextId++;
      return new Promise((resolve) => {
        pending.set(id, resolve);
        pyWorker.postMessage({ type: 'run', id, code });
      });
    },
    [] // reads module-level vars directly; re-render handled by tick
  );

  // loadPackage — installs a package via micropip (supports PyPI packages + Pyodide built-ins)
  // Usage: await loadPackage('numpy')  or  await loadPackage(['numpy', 'pandas'])
  const loadPackage = useCallback((packages) => {
    if (!pyWorker || !pyReady) {
      return Promise.resolve({ success: false, error: 'Runtime not ready — wait for Python to load.' });
    }
    const id = nextId++;
    const pkgArray = Array.isArray(packages) ? packages : [packages];
    return new Promise((resolve) => {
      pending.set(id, resolve);
      pyWorker.postMessage({ type: 'loadPackage', id, packages: pkgArray });
    });
  }, []);

  return {
    ready: pyReady,
    loading: !pyReady && !pyError,
    error: pyError,
    runPython,
    loadPackage,
  };
}
